<template>
  <div class="config-page">
    <div class="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-3 tw-mb-4">
      <div>
        <h1 class="bk-display tw-text-2xl tw-mb-1">Connect MLS</h1>
        <p class="tw-text-muted tw-mb-0 tw-text-sm">
          Three things: credentials, where listings go, and Save. Everything else is optional.
        </p>
      </div>
      <div class="tw-flex tw-flex-wrap tw-gap-2">
        <Button
          label="Reload"
          icon="pi pi-refresh"
          class="p-button-outlined p-button-secondary p-button-sm"
          :disabled="loading || saving"
          @click="reload"
        />
        <Button
          label="Save"
          icon="pi pi-save"
          class="p-button-sm"
          :loading="saving"
          :disabled="loading || !config"
          @click="save"
        />
      </div>
    </div>

    <Message v-if="error" severity="error" :closable="false" class="tw-mb-3">{{ error }}</Message>
    <Message
      v-if="saveMessage"
      :severity="saveOk ? 'success' : 'warn'"
      :closable="true"
      class="tw-mb-3"
      @close="saveMessage = null"
    >
      {{ saveMessage }}
    </Message>

    <div v-if="loading" class="bk-panel tw-py-16 tw-text-center">
      <ProgressSpinner style="width:40px;height:40px" strokeWidth="4" />
    </div>

    <div v-else-if="config" class="config-layout">
      <aside class="config-rail bk-panel tw-p-3">
        <div class="tw-px-2 tw-pt-1 tw-pb-2 tw-flex tw-items-center tw-justify-between">
          <span class="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-muted">MLS feeds</span>
          <Button
            icon="pi pi-plus"
            label="Add"
            class="p-button-sm p-button-outlined"
            @click="toggleAddMenu"
            aria-haspopup="true"
            aria-controls="add_source_menu"
          />
          <Menu id="add_source_menu" ref="addMenu" :model="addSourceItems" :popup="true" />
        </div>

        <button
          v-for="(source, sIdx) in config.sources"
          :key="sIdx"
          type="button"
          class="rail-item"
          :class="{ 'is-active': view === 'source' && selectedIdx === sIdx }"
          @click="openSource(sIdx)"
        >
          <span class="rail-title">{{ source.name || '(unnamed)' }}</span>
          <span class="rail-sub">{{ platformLabel(source.platformAdapterName) }} · {{ destinationShort(source) }}</span>
        </button>

        <div v-if="!config.sources.length" class="tw-px-3 tw-py-4 tw-text-sm tw-text-muted">
          No feeds yet — click Add.
        </div>

        <div class="tw-mt-4 tw-pt-3" style="border-top:1px solid #e4e4e7">
          <button
            type="button"
            class="rail-item"
            :class="{ 'is-active': view === 'server' }"
            @click="view = 'server'"
          >
            <span class="rail-title">App settings</span>
            <span class="rail-sub">Port · rarely change</span>
          </button>
        </div>
      </aside>

      <section class="config-stage bk-panel tw-p-5">
        <div v-if="view === 'server'">
          <h2 class="bk-display tw-text-xl tw-mb-1">App settings</h2>
          <p class="tw-text-muted tw-mb-4 tw-text-sm">Leave these alone unless you know you need to change them.</p>
          <div class="field">
            <label class="field-label">Config version</label>
            <InputText v-model="config.userConfigVersion" />
          </div>
          <div class="field">
            <label class="field-label">API port</label>
            <InputNumber v-model="config.server.port" :useGrouping="false" />
          </div>
          <div class="field">
            <label class="field-label">Stats database</label>
            <InputText v-model="config.database.connectionString" />
          </div>
        </div>

        <div v-else-if="view === 'source' && !selectedSource" class="tw-py-10 tw-text-center">
          <h2 class="bk-display tw-text-2xl tw-mb-2">Connect your first MLS</h2>
          <p class="tw-text-muted tw-mb-4">Pick the vendor that issued your API key.</p>
          <div class="tw-flex tw-flex-wrap tw-justify-center tw-gap-2">
            <Button label="MLS Grid" @click="addSourceAndOpen('mlsGrid')" />
            <Button label="Bridge" class="p-button-outlined" @click="addSourceAndOpen('bridgeInteractive')" />
            <Button label="Trestle" class="p-button-outlined" @click="addSourceAndOpen('trestle')" />
            <Button label="RMLS" class="p-button-outlined" @click="addSourceAndOpen('rmls')" />
          </div>
        </div>

        <div v-else-if="selectedSource">
          <div class="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-3 tw-mb-4">
            <div>
              <h2 class="bk-display tw-text-2xl tw-mb-1">{{ selectedSource.name || 'Untitled feed' }}</h2>
              <p class="tw-text-muted tw-mb-0 tw-text-sm">{{ platformLabel(selectedSource.platformAdapterName) }}</p>
            </div>
            <div class="tw-flex tw-gap-2">
              <Button
                v-if="selectedSource.platformAdapterName === 'mlsGrid'"
                label="Apply Grid demo defaults"
                icon="pi pi-bolt"
                class="p-button-outlined p-button-sm"
                @click="applyMlsGridDemoDefaults"
              />
              <Button
                label="Remove"
                icon="pi pi-trash"
                class="p-button-outlined p-button-danger p-button-sm"
                @click="removeSelectedSource"
              />
            </div>
          </div>

          <div class="setup-checklist tw-mb-5">
            <div
              v-for="step in setupSteps"
              :key="step.id"
              class="setup-step"
              :class="{ 'is-done': step.done }"
            >
              <i :class="step.done ? 'pi pi-check-circle' : 'pi pi-circle'" aria-hidden="true" />
              <span>{{ step.label }}</span>
            </div>
          </div>

          <!-- 1. Basics -->
          <section class="simple-card tw-mb-4">
            <h3 class="simple-card-title">1. Credentials</h3>
            <p class="simple-card-help">The API key from your MLS vendor (or Grid demo token).</p>
            <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
              <div class="field">
                <label class="field-label">Feed nickname</label>
                <InputText v-model="selectedSource.name" placeholder="e.g. stellar-demo" />
              </div>
              <div class="field">
                <label class="field-label">Vendor</label>
                <Dropdown
                  v-model="selectedSource.platformAdapterName"
                  :options="platformOptions"
                  optionLabel="text"
                  optionValue="value"
                  class="tw-w-full"
                  @change="onPlatformChange(selectedSource)"
                />
              </div>
            </div>
            <div class="field">
              <label class="field-label">Access token</label>
              <Password v-model="selectedSource.accessToken" :feedback="false" toggleMask class="tw-w-full" />
            </div>
            <div
              v-if="selectedSource.platformAdapterName === 'bridgeInteractive' || selectedSource.platformAdapterName === 'trestle'"
              class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4"
            >
              <div class="field">
                <label class="field-label">Client ID</label>
                <InputText v-model="selectedSource.clientId" />
              </div>
              <div class="field">
                <label class="field-label">Client secret</label>
                <Password v-model="selectedSource.clientSecret" :feedback="false" toggleMask class="tw-w-full" />
              </div>
            </div>
            <div v-if="selectedSource.platformAdapterName === 'mlsGrid'" class="field">
              <label class="field-label">MLS system code</label>
              <InputText
                v-model="ensureEndpointConfig(selectedSource).originatingSystemName"
                placeholder="e.g. mfrmls for Stellar demo"
              />
              <small class="field-help">From Grid docs — Stellar demo uses <code>mfrmls</code>.</small>
            </div>
          </section>

          <!-- 2. Destination -->
          <section class="simple-card tw-mb-4">
            <h3 class="simple-card-title">2. Where listings go</h3>
            <p class="simple-card-help">Almost always Brokurz warehouse so Website Builder sites can show them.</p>

            <div class="dest-choices">
              <button
                type="button"
                class="dest-choice"
                :class="{ 'is-on': destinationMode === 'warehouse' }"
                @click="destinationMode = 'warehouse'"
              >
                <span>
                  <strong>Brokurz warehouse</strong>
                  <em>Live inventory for sites (recommended)</em>
                </span>
              </button>
              <button
                type="button"
                class="dest-choice"
                :class="{ 'is-on': destinationMode === 'devnull' }"
                @click="destinationMode = 'devnull'"
              >
                <span>
                  <strong>Test only</strong>
                  <em>Download API data, don’t save listings</em>
                </span>
              </button>
              <button
                type="button"
                class="dest-choice"
                :class="{ 'is-on': destinationMode === 'other' }"
                @click="destinationMode = 'other'"
              >
                <span>
                  <strong>Other database</strong>
                  <em>MySQL / Solr — advanced</em>
                </span>
              </button>
            </div>

            <div v-if="destinationMode === 'warehouse'" class="field tw-mt-3">
              <label class="field-label">Board slug</label>
              <InputText
                v-model="warehouseFeedSlug"
                placeholder="e.g. stellar"
              />
              <small class="field-help">
                Must match a board on the <router-link to="/boards">Boards</router-link> page (activate it first).
              </small>
            </div>

            <div v-else-if="destinationMode === 'other'" class="tw-mt-3">
              <div
                v-for="(dest, dIdx) in selectedSource.destinations"
                :key="dIdx"
                class="config-block tw-mb-2"
              >
                <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3">
                  <div class="field">
                    <label class="field-label">Type</label>
                    <Dropdown
                      v-model="dest.type"
                      :options="destinationTypeOptions"
                      optionLabel="label"
                      optionValue="value"
                      class="tw-w-full"
                    />
                  </div>
                  <div v-if="dest.type === 'mysql'" class="field">
                    <label class="field-label">MySQL URL</label>
                    <InputText v-model="ensureDestConfig(dest).connectionString" />
                  </div>
                  <template v-else-if="dest.type === 'solr'">
                    <div class="field">
                      <label class="field-label">Solr host</label>
                      <InputText v-model="ensureDestConfig(dest).host" />
                    </div>
                    <div class="field">
                      <label class="field-label">Solr port</label>
                      <InputNumber v-model="ensureDestConfig(dest).port" :useGrouping="false" />
                    </div>
                  </template>
                  <div v-else-if="dest.type === 'listingsWarehouse'" class="field">
                    <label class="field-label">Board slug</label>
                    <InputText v-model="ensureDestConfig(dest).feedSlug" />
                  </div>
                </div>
              </div>
              <Button label="Add MySQL" class="p-button-outlined p-button-sm tw-mr-2" @click="addDestination(selectedSource, 'mysql')" />
              <Button label="Add Solr" class="p-button-outlined p-button-sm" @click="addDestination(selectedSource, 'solr')" />
            </div>
          </section>

          <!-- 3. What to pull -->
          <section class="simple-card tw-mb-4">
            <h3 class="simple-card-title">3. What to sync</h3>
            <div class="field-row tw-mb-3">
              <InputSwitch v-model="activeListingsOnly" inputId="activeOnly" />
              <label for="activeOnly">Active listings only</label>
            </div>
            <div class="field-row">
              <InputSwitch v-model="includePhotos" inputId="includePhotos" />
              <label for="includePhotos">Include photos</label>
            </div>
          </section>

          <!-- 4. Schedule -->
          <section class="simple-card tw-mb-4">
            <h3 class="simple-card-title">4. Auto-update</h3>
            <p class="simple-card-help">Keep off until the first manual sync looks good.</p>
            <div class="field">
              <label class="field-label">How often</label>
              <Dropdown
                v-model="syncSchedulePreset"
                :options="scheduleOptions"
                optionLabel="label"
                optionValue="value"
                class="tw-w-full md:tw-w-80"
              />
            </div>
          </section>

          <!-- Advanced -->
          <section class="simple-card simple-card--muted">
            <button type="button" class="advanced-toggle" @click="showAdvanced = !showAdvanced">
              <i :class="showAdvanced ? 'pi pi-chevron-down' : 'pi pi-chevron-right'" aria-hidden="true" />
              Advanced (API URLs, filters, page size)
            </button>
            <div v-if="showAdvanced" class="tw-mt-4">
              <Message severity="info" :closable="false" class="tw-mb-3">
                You usually don’t need this. Defaults are filled in when you pick a vendor.
              </Message>
              <div class="field">
                <label class="field-label">API base URL</label>
                <InputText v-model="ensureEndpointConfig(selectedSource).odataBaseUrl" />
              </div>
              <div class="field">
                <label class="field-label">Metadata URL</label>
                <InputText v-model="selectedSource.metadataEndpoint" />
              </div>
              <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-3">
                <div class="field">
                  <label class="field-label">Listings per page</label>
                  <InputNumber v-model="selectedSource.top" :useGrouping="false" />
                </div>
                <div class="field">
                  <label class="field-label">Pause between pages (ms)</label>
                  <InputNumber v-model="selectedSource.requestDelayMs" :min="0" :useGrouping="false" />
                </div>
                <div class="field">
                  <label class="field-label">Purge page size</label>
                  <InputNumber v-model="selectedSource.topForPurge" :useGrouping="false" />
                </div>
              </div>
              <div v-if="selectedSource.platformAdapterName === 'mlsGrid'" class="field-row tw-mb-3">
                <InputSwitch v-model="ensureEndpointConfig(selectedSource).requireCanView" inputId="requireCanView" />
                <label for="requireCanView">Only listings Grid says we can view</label>
              </div>
              <div class="field">
                <label class="field-label">Extra Property filter (optional)</label>
                <InputText
                  :value="resourceFilterValue(selectedSource, 'Property')"
                  :placeholder="mlsGridFilterPlaceholder(selectedSource)"
                  @input="onFilterInput(selectedSource, 'Property', $event)"
                />
                <small v-if="selectedSource.platformAdapterName === 'mlsGrid'" class="field-help">
                  Grid allows: StandardStatus, ListingId, PropertyType, ListOfficeMlsId (plus system fields we add automatically).
                </small>
              </div>
              <div class="field-row tw-mb-2">
                <InputSwitch v-model="selectedSource.useOrderBy" inputId="useOrderBy" />
                <label for="useOrderBy">Use $orderby</label>
              </div>
              <div class="field-row tw-mb-3">
                <InputSwitch v-model="selectedSource.useCustomEndpoints" inputId="customEndpoints" />
                <label for="customEndpoints">Use custom endpoint JS (experts only)</label>
              </div>
              <div v-if="selectedSource.useCustomEndpoints">
                <div v-for="fnKey in sourceFnKeys" :key="fnKey" class="field">
                  <label class="field-label">{{ fnKey }}</label>
                  <Textarea v-model="selectedSource[fnKey]" rows="3" class="tw-font-mono tw-text-sm tw-w-full" />
                </div>
              </div>
              <div class="tw-font-semibold tw-mb-2 tw-mt-3">Resources</div>
              <div
                v-for="(res, rIdx) in selectedSource.mlsResources"
                :key="rIdx"
                class="config-block tw-mb-2"
              >
                <div class="tw-flex tw-justify-between tw-mb-2">
                  <InputText v-model="res.name" class="tw-w-40" />
                  <Button icon="pi pi-times" class="p-button-text p-button-danger p-button-sm" @click="selectedSource.mlsResources.splice(rIdx, 1)" />
                </div>
                <small class="field-help">Photos = expand Media (toggle above handles this).</small>
              </div>
              <Button
                label="Add resource"
                class="p-button-outlined p-button-sm"
                @click="selectedSource.mlsResources.push({ name: 'Property', select: [], expand: [] })"
              />
            </div>
          </section>
        </div>
      </section>
    </div>

    <div v-if="config" class="config-sticky-save">
      <span class="tw-text-sm tw-text-muted tw-hidden sm:tw-inline">Save, then run sync from the feed’s Sync page.</span>
      <Button label="Save" icon="pi pi-save" :loading="saving" @click="save" />
    </div>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import { defaultEndpointConfigForPlatform } from './configEditorDefaults'

const SOURCE_FN_KEYS = [
  'getResourceEndpoint',
  'getReplicationEndpoint',
  'getPurgeEndpoint',
  'getReconcileEndpoint',
]

const ACTIVE_FILTER = "StandardStatus eq 'Active'"

export default {
  data() {
    return {
      loading: true,
      saving: false,
      error: null,
      saveMessage: null,
      saveOk: false,
      config: null,
      view: 'source',
      selectedIdx: 0,
      showAdvanced: false,
      sourceFnKeys: SOURCE_FN_KEYS,
      scheduleOptions: [
        { value: 'off', label: 'Manual only (recommended until first sync works)' },
        { value: '15m', label: 'Every 15 minutes' },
        { value: '1h', label: 'Every hour' },
        { value: '6h', label: 'Every 6 hours' },
        { value: 'daily', label: 'Once a day' },
      ],
      platformOptions: [
        { value: 'mlsGrid', text: 'MLS Grid' },
        { value: 'bridgeInteractive', text: 'Bridge Interactive' },
        { value: 'trestle', text: 'Trestle' },
        { value: 'rmls', text: 'RMLS' },
      ],
      destinationTypeOptions: [
        { label: 'Brokurz warehouse', value: 'listingsWarehouse' },
        { label: 'MySQL', value: 'mysql' },
        { label: 'Solr', value: 'solr' },
        { label: 'Test only', value: 'devnull' },
      ],
      addSourceItems: [
        { label: 'MLS Grid', command: () => this.addSourceAndOpen('mlsGrid') },
        { label: 'Bridge Interactive', command: () => this.addSourceAndOpen('bridgeInteractive') },
        { label: 'Trestle', command: () => this.addSourceAndOpen('trestle') },
        { label: 'RMLS', command: () => this.addSourceAndOpen('rmls') },
      ],
    }
  },
  computed: {
    selectedSource() {
      if (!this.config || this.view !== 'source') return null
      return this.config.sources[this.selectedIdx] || null
    },
    setupSteps() {
      const s = this.selectedSource
      if (!s) return []
      const hasToken = !!(s.accessToken && String(s.accessToken).trim())
      const dest = (s.destinations || [])[0]
      const hasDest = !!dest
      const warehouseOk = dest && dest.type === 'listingsWarehouse'
        ? !!(dest.config && dest.config.feedSlug)
        : dest && dest.type !== 'listingsWarehouse'
      return [
        { id: 'token', label: 'Access token', done: hasToken },
        { id: 'dest', label: 'Where listings go', done: hasDest && warehouseOk },
        { id: 'save', label: 'Save, then Sync', done: false },
      ]
    },
    destinationMode: {
      get() {
        const dest = this.selectedSource && (this.selectedSource.destinations || [])[0]
        if (!dest) return 'warehouse'
        if (dest.type === 'listingsWarehouse') return 'warehouse'
        if (dest.type === 'devnull') return 'devnull'
        return 'other'
      },
      set(mode) {
        const source = this.selectedSource
        if (!source) return
        if (mode === 'warehouse') {
          const slug = this.warehouseFeedSlug || 'stellar'
          this.$set(source, 'destinations', [{
            type: 'listingsWarehouse',
            name: 'warehouse1',
            config: { feedSlug: slug },
          }])
        } else if (mode === 'devnull') {
          this.$set(source, 'destinations', [{ type: 'devnull', name: 'devnull1', config: {} }])
        } else if (!(source.destinations || []).some(d => d.type === 'mysql' || d.type === 'solr')) {
          this.$set(source, 'destinations', [{
            type: 'mysql',
            name: 'mysql1',
            config: { connectionString: 'mysql://user:password@localhost:3306/mls' },
          }])
        }
      },
    },
    warehouseFeedSlug: {
      get() {
        const dest = (this.selectedSource && this.selectedSource.destinations || [])
          .find(d => d.type === 'listingsWarehouse')
        return (dest && dest.config && dest.config.feedSlug) || ''
      },
      set(slug) {
        const source = this.selectedSource
        if (!source) return
        let dest = (source.destinations || []).find(d => d.type === 'listingsWarehouse')
        if (!dest) {
          dest = { type: 'listingsWarehouse', name: 'warehouse1', config: {} }
          this.$set(source, 'destinations', [dest])
        }
        if (!dest.config) this.$set(dest, 'config', {})
        this.$set(dest.config, 'feedSlug', slug)
      },
    },
    activeListingsOnly: {
      get() {
        const f = this.resourceFilterValue(this.selectedSource, 'Property')
        return f.includes("StandardStatus eq 'Active'")
      },
      set(on) {
        const source = this.selectedSource
        if (!source) return
        const current = this.resourceFilterValue(source, 'Property')
        if (on) {
          if (!current.includes("StandardStatus eq 'Active'")) {
            this.setResourceFilter(source, 'Property', ACTIVE_FILTER)
          }
        } else if (current.trim() === ACTIVE_FILTER) {
          this.setResourceFilter(source, 'Property', '')
        }
      },
    },
    includePhotos: {
      get() {
        const prop = this.propertyResource
        if (!prop) return false
        return (prop.expand || []).some(e => (e.name || e.fieldName) === 'Media')
      },
      set(on) {
        const prop = this.ensurePropertyResource()
        if (!prop) return
        if (!Array.isArray(prop.expand)) this.$set(prop, 'expand', [])
        const idx = prop.expand.findIndex(e => (e.name || e.fieldName) === 'Media')
        if (on && idx < 0) {
          prop.expand.push({ name: 'Media', fieldName: 'Media', purgeFromParent: true })
        } else if (!on && idx >= 0) {
          prop.expand.splice(idx, 1)
        }
      },
    },
    propertyResource() {
      const s = this.selectedSource
      if (!s || !s.mlsResources) return null
      return s.mlsResources.find(r => r.name === 'Property') || s.mlsResources[0] || null
    },
    syncSchedulePreset: {
      get() {
        const cron = this.selectedSource && this.selectedSource.cron && this.selectedSource.cron.sync
        if (!cron || !cron.enabled) return 'off'
        const s = (cron.cronStrings || [])[0] || ''
        if (s === '*/15 * * * *') return '15m'
        if (s === '0 * * * *') return '1h'
        if (s === '0 */6 * * *') return '6h'
        if (s === '0 4 * * *') return 'daily'
        return '15m'
      },
      set(preset) {
        const source = this.selectedSource
        if (!source) return
        const sync = this.ensureCron(source, 'sync')
        if (preset === 'off') {
          sync.enabled = false
          return
        }
        sync.enabled = true
        const map = {
          '15m': '*/15 * * * *',
          '1h': '0 * * * *',
          '6h': '0 */6 * * *',
          daily: '0 4 * * *',
        }
        sync.cronStrings = [map[preset] || '*/15 * * * *']
      },
    },
  },
  created() {
    this.reload()
  },
  methods: {
    eventValue(e) {
      if (e == null) return ''
      if (typeof e === 'string' || typeof e === 'number') return String(e)
      if (e.target && e.target.value != null) return e.target.value
      return String(e)
    },
    onFilterInput(source, resourceName, e) {
      this.setResourceFilter(source, resourceName, this.eventValue(e))
    },
    toggleAddMenu(event) {
      this.$refs.addMenu.toggle(event)
    },
    platformLabel(name) {
      const found = this.platformOptions.find(p => p.value === name)
      return found ? found.text : name
    },
    destinationShort(source) {
      const d = (source.destinations || [])[0]
      if (!d) return 'no destination'
      if (d.type === 'listingsWarehouse') return `warehouse · ${((d.config || {}).feedSlug) || '?'}`
      if (d.type === 'devnull') return 'test only'
      return d.type
    },
    openSource(idx) {
      this.view = 'source'
      this.selectedIdx = idx
      this.showAdvanced = false
    },
    ensurePropertyResource() {
      const source = this.selectedSource
      if (!source) return null
      if (!Array.isArray(source.mlsResources)) this.$set(source, 'mlsResources', [])
      let prop = source.mlsResources.find(r => r.name === 'Property')
      if (!prop) {
        prop = { name: 'Property', select: [], expand: [] }
        source.mlsResources.unshift(prop)
      }
      return prop
    },
    applyMlsGridDemoDefaults() {
      const source = this.selectedSource
      if (!source) return
      source.name = 'stellar-demo'
      source.platformAdapterName = 'mlsGrid'
      source.metadataEndpoint = 'https://api-demo.mlsgrid.com/v2/$metadata'
      source.top = 100
      source.requestDelayMs = 500
      source.useCustomEndpoints = false
      this.$set(source, 'endpointConfig', {
        odataBaseUrl: 'https://api-demo.mlsgrid.com/v2',
        originatingSystemName: 'mfrmls',
        requireCanView: true,
        resourceFilters: { Property: ACTIVE_FILTER },
      })
      this.$set(source, 'mlsResources', [{
        name: 'Property',
        select: [],
        expand: [{ name: 'Media', fieldName: 'Media', purgeFromParent: true }],
      }])
      this.$set(source, 'destinations', [{
        type: 'listingsWarehouse',
        name: 'warehouse1',
        config: { feedSlug: 'stellar' },
      }])
      this.ensureCron(source, 'sync').enabled = false
      this.$toast.add({
        severity: 'success',
        summary: 'Demo defaults applied',
        detail: 'Paste your Grid demo token, Save, then Sync.',
        life: 4000,
      })
    },
    async reload() {
      this.loading = true
      this.error = null
      this.saveMessage = null
      try {
        const { data } = await this.$apollo.query({
          query: gql`query { editableConfig }`,
          fetchPolicy: 'no-cache',
        })
        this.config = this.normalizeConfig(data.editableConfig)
        if (this.config.sources.length) {
          this.view = 'source'
          this.selectedIdx = 0
        } else {
          this.view = 'source'
        }
      } catch (e) {
        this.error = e.message
      } finally {
        this.loading = false
      }
    },
    normalizeConfig(raw) {
      const config = JSON.parse(JSON.stringify(raw || {}))
      if (!config.server) config.server = { port: 4000 }
      if (!config.database) config.database = { connectionString: '' }
      if (!Array.isArray(config.sources)) config.sources = []
      if (!config.userConfigVersion) config.userConfigVersion = '0.3.0'
      config.sources.forEach(source => {
        if (!source.endpointConfig) {
          source.endpointConfig = defaultEndpointConfigForPlatform(source.platformAdapterName)
        }
        if (!source.endpointConfig.resourceFilters) {
          source.endpointConfig.resourceFilters = {}
        }
        if (source.platformAdapterName === 'mlsGrid' && source.endpointConfig.requireCanView == null) {
          source.endpointConfig.requireCanView = true
        }
        if (source.useCustomEndpoints == null) {
          source.useCustomEndpoints = !!(
            source.getResourceEndpoint &&
            !(source.endpointConfig && source.endpointConfig.odataBaseUrl)
          )
        }
        if (source.requestDelayMs == null) source.requestDelayMs = 0
        if (!Array.isArray(source.mlsResources)) source.mlsResources = []
        source.mlsResources.forEach(res => {
          if (!Array.isArray(res.select)) res.select = res.select ? [].concat(res.select) : []
          if (!Array.isArray(res.expand)) res.expand = []
          res.expand.forEach(exp => {
            if (exp.purgeFromParent == null) exp.purgeFromParent = false
            if (exp.fieldName == null) exp.fieldName = exp.name || ''
          })
        })
        if (!Array.isArray(source.destinations)) source.destinations = []
        source.destinations.forEach(dest => {
          if (!dest.config) dest.config = {}
        })
        if (!source.cron) source.cron = {}
      })
      return config
    },
    ensureEndpointConfig(source) {
      if (!source.endpointConfig) {
        this.$set(source, 'endpointConfig', defaultEndpointConfigForPlatform(source.platformAdapterName))
      }
      if (!source.endpointConfig.resourceFilters) {
        this.$set(source.endpointConfig, 'resourceFilters', {})
      }
      if (source.platformAdapterName === 'mlsGrid' && source.endpointConfig.requireCanView == null) {
        this.$set(source.endpointConfig, 'requireCanView', true)
      }
      return source.endpointConfig
    },
    ensureDestConfig(dest) {
      if (!dest.config) this.$set(dest, 'config', {})
      return dest.config
    },
    ensureCron(source, jobType) {
      if (!source.cron) this.$set(source, 'cron', {})
      if (!source.cron[jobType]) {
        this.$set(source.cron, jobType, { enabled: false, cronStrings: [] })
      }
      if (source.cron[jobType].enabled == null) {
        this.$set(source.cron[jobType], 'enabled', false)
      }
      if (!source.cron[jobType].cronStrings) {
        this.$set(source.cron[jobType], 'cronStrings', [])
      }
      return source.cron[jobType]
    },
    onPlatformChange(source) {
      if (!source.useCustomEndpoints) {
        this.$set(source, 'endpointConfig', defaultEndpointConfigForPlatform(source.platformAdapterName))
      }
    },
    resourceFilterValue(source, resourceName) {
      if (!source || !resourceName) return ''
      const filters = (source.endpointConfig && source.endpointConfig.resourceFilters) || {}
      return filters[resourceName] || ''
    },
    setResourceFilter(source, resourceName, value) {
      if (!resourceName) return
      const cfg = this.ensureEndpointConfig(source)
      if (!cfg.resourceFilters) this.$set(cfg, 'resourceFilters', {})
      const trimmed = String(value || '').trim()
      if (trimmed) {
        this.$set(cfg.resourceFilters, resourceName, trimmed)
      } else if (cfg.resourceFilters[resourceName] != null) {
        this.$delete(cfg.resourceFilters, resourceName)
      }
    },
    splitCsv(text) {
      return String(text || '')
        .split(',')
        .map(s => s.trim())
        .filter(Boolean)
    },
    mlsGridFilterPlaceholder(source) {
      return source && source.platformAdapterName === 'mlsGrid'
        ? "e.g. StandardStatus eq 'Active'"
        : "e.g. City eq 'Austin'"
    },
    async addSourceAndOpen(platformAdapterName) {
      try {
        const { data } = await this.$apollo.query({
          query: gql`query ($platformAdapterName: String!) {
            sourceTemplate(platformAdapterName: $platformAdapterName)
          }`,
          variables: { platformAdapterName },
          fetchPolicy: 'no-cache',
        })
        this.config.sources.push(this.normalizeConfig({ sources: [data.sourceTemplate] }).sources[0])
        this.openSource(this.config.sources.length - 1)
        this.$toast.add({
          severity: 'success',
          summary: 'Feed added',
          detail: 'Fill in token + board slug, then Save.',
          life: 3000,
        })
      } catch (e) {
        this.error = e.message
      }
    },
    removeSelectedSource() {
      if (this.selectedIdx == null || !this.config.sources[this.selectedIdx]) return
      const name = this.config.sources[this.selectedIdx].name
      this.$confirm.require({
        message: `Remove feed "${name}"?`,
        header: 'Remove MLS feed',
        icon: 'pi pi-exclamation-triangle',
        acceptClass: 'p-button-danger',
        accept: () => {
          this.config.sources.splice(this.selectedIdx, 1)
          if (!this.config.sources.length) {
            this.view = 'source'
            this.selectedIdx = 0
          } else {
            this.selectedIdx = Math.min(this.selectedIdx, this.config.sources.length - 1)
          }
        },
      })
    },
    addDestination(source, type) {
      const destName = `${type}${source.destinations.length + 1}`
      const dest = { type, name: destName, config: {} }
      if (type === 'mysql') {
        dest.config.connectionString = 'mysql://user:password@localhost:3306/mls'
      } else if (type === 'solr') {
        dest.config.host = 'localhost'
        dest.config.port = 8983
      } else if (type === 'listingsWarehouse') {
        dest.config.feedSlug = 'stellar'
      }
      source.destinations.push(dest)
    },
    async save() {
      this.saving = true
      this.error = null
      this.saveMessage = null
      try {
        const { data } = await this.$apollo.mutate({
          mutation: gql`mutation ($config: JSON!) {
            saveEditableConfig(config: $config) {
              ok
              configSource
              restartRecommended
              message
            }
          }`,
          variables: { config: this.config },
        })
        const result = data.saveEditableConfig
        this.saveOk = result.ok
        this.saveMessage = result.message
        this.$apollo.getClient().reFetchObservableQueries()
        this.$toast.add({
          severity: result.ok ? 'success' : 'warn',
          summary: result.ok ? 'Saved' : 'Saved with notice',
          detail: result.message,
          life: 4000,
        })
      } catch (e) {
        this.error = e.message
        this.saveOk = false
        this.$toast.add({ severity: 'error', summary: 'Save failed', detail: e.message, life: 5000 })
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.config-layout {
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr);
  gap: 1rem;
  align-items: start;
}

.rail-item {
  display: block;
  width: 100%;
  text-align: left;
  border: 1px solid transparent;
  background: transparent;
  border-radius: 6px;
  padding: 0.65rem 0.75rem;
  margin-bottom: 0.25rem;
  cursor: pointer;
  transition: background 0.12s ease;
}

.rail-item:hover {
  background: rgba(24, 24, 27, 0.05);
}

.rail-item.is-active {
  background: rgba(24, 24, 27, 0.09);
  font-weight: 600;
}

.rail-title {
  display: block;
  font-weight: 600;
  font-size: 0.875rem;
}

.rail-sub {
  display: block;
  font-size: 0.75rem;
  opacity: 0.65;
  margin-top: 0.15rem;
}

.setup-checklist {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1.25rem;
}

.setup-step {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.875rem;
  color: #71717a;
}

.setup-step.is-done {
  color: #15803d;
  font-weight: 600;
}

.simple-card {
  border: 1px solid #e4e4e7;
  border-radius: 10px;
  padding: 1.1rem 1.15rem;
  background: #fff;
}

.simple-card--muted {
  background: #fafafa;
}

.simple-card-title {
  font-size: 1rem;
  font-weight: 700;
  margin: 0 0 0.25rem;
}

.simple-card-help {
  margin: 0 0 0.9rem;
  font-size: 0.8125rem;
  color: #71717a;
}

.dest-choices {
  display: grid;
  gap: 0.5rem;
}

.dest-choice {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  padding: 0.75rem 0.85rem;
  border: 1px solid #e4e4e7;
  border-radius: 8px;
  cursor: pointer;
  background: #fafafa;
  text-align: left;
  width: 100%;
  font: inherit;
  color: inherit;
}

.dest-choice.is-on {
  border-color: #18181b;
  background: #fff;
}

.dest-choice strong {
  display: block;
  font-size: 0.9rem;
}

.dest-choice em {
  display: block;
  font-style: normal;
  font-size: 0.78rem;
  color: #71717a;
  margin-top: 0.15rem;
}

.advanced-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: 0;
  background: transparent;
  padding: 0;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  color: #3f3f46;
}

.config-block {
  border: 1px solid #e4e4e7;
  border-radius: 8px;
  padding: 1rem;
  background: #fafafa;
}

.config-sticky-save {
  position: sticky;
  bottom: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
  padding: 0.75rem 0;
  background: linear-gradient(180deg, transparent, #f4f4f5 40%);
  z-index: 5;
}

@media (max-width: 900px) {
  .config-layout {
    grid-template-columns: 1fr;
  }
}
</style>
